/**
 * ImporterConnection — back-office sync-status badge. Ported from the
 * production `apps/dop/src/features/sales-tracker-v2/components/importer-connection.tsx`
 * with native deps removed (this app is web-only): `moment` → native
 * `Date`/`Intl`; `Linking`/`Platform` → plain `window.open`. `useSiteTime` is
 * KEPT for correct site-local time display.
 *
 * Reads the site-level `syncInfo` from the same merged TeamMembers response
 * (no separate call). Falls back to plain vendor-name text when there's no
 * logo, and hides the external-link arrow when there's no portal URL — the
 * real sales-tracker component doesn't have this fallback yet; worth folding
 * back there too, but that's out of scope for this PR.
 */
import { Box, Divider, HStack, IconButton, Image, Popover, Pressable, VStack, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';

import { ArrowSquareUpRightIcon, ClockRefreshIcon, InfoCircleIcon, Text } from '@pimm/base';
import { useSiteTime } from '@app/features/store-core';
import type { SyncInfoDto } from '../types';

type ImporterConnectionProps = {
  syncInfo?: SyncInfoDto;
};

// Mirrors moment.utc(iso).utcOffset(offsetMinutes).format('DD/MM/YYYY h:mm A') —
// parse as UTC, shift by the site's UTC offset, then read the shifted instant
// back out with UTC getters (avoids the browser's own local timezone being
// applied a second time).
const formatSiteLocalDateTime = (isoUtc: string, utcOffsetMinutes: number): string => {
  const shifted = new Date(new Date(isoUtc).getTime() + utcOffsetMinutes * 60 * 1000);
  const dd = String(shifted.getUTCDate()).padStart(2, '0');
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = shifted.getUTCFullYear();
  const hours24 = shifted.getUTCHours();
  const minutes = String(shifted.getUTCMinutes()).padStart(2, '0');
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${dd}/${mm}/${yyyy} ${hours12}:${minutes} ${ampm}`;
};

export const ImporterConnection = ({ syncInfo }: ImporterConnectionProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const siteTime = useSiteTime();
  const isOffline = syncInfo?.status?.toLowerCase() !== 'online';
  const isImporterEnabled = syncInfo?.status?.toLowerCase() !== 'not enabled';
  const importerStatus = t(
    isImporterEnabled
      ? isOffline
        ? 'store_settings:sync_status_offline'
        : 'store_settings:sync_status_online'
      : 'store_settings:sync_status_not_enabled',
  );
  const lastImportDateTime = syncInfo?.lastDataSyncUtc;

  const lastImportDateTimeText = syncInfo?.lastDataSyncUtc
    ? formatSiteLocalDateTime(syncInfo.lastDataSyncUtc, siteTime.utcOffsetInMinutes ?? 0)
    : '—';

  const hasLogo = !!syncInfo?.vendorInfo?.logoImgUrl;
  const hasPortalUrl = !!syncInfo?.vendorInfo?.vendorPortalUrl;

  const handleOpenBlankPage = () => {
    if (syncInfo?.vendorInfo?.vendorPortalUrl) window.open(syncInfo.vendorInfo.vendorPortalUrl, '_blank', 'noreferrer');
  };

  return (
    <HStack space={2} alignItems="center" rounded="lg" py={0.5} pl={1} pr={2} h={10} borderWidth={1} borderColor="gray.300">
      <Pressable onPress={handleOpenBlankPage} isDisabled={!hasPortalUrl}>
        <HStack space={2} alignItems="center" pl={1.5}>
          {hasLogo ? (
            <Box w={60} h={9}>
              <Image
                source={{ uri: syncInfo?.vendorInfo?.logoImgUrl }}
                style={{ flex: 1, width: '100%', height: '100%', resizeMode: 'contain' }}
                alt={syncInfo?.vendorInfo?.vendorPortalUrl ?? syncInfo?.vendorInfo?.fullName ?? t('store_settings:sync_vendor_logo_alt')}
              />
            </Box>
          ) : (
            <Text size="sm" fontWeight={600} color="gray.700">
              {syncInfo?.vendorInfo?.fullName ?? '—'}
            </Text>
          )}
          {hasPortalUrl && <ArrowSquareUpRightIcon size="md" color={colors.gray[700]} />}
        </HStack>
      </Pressable>

      <Divider orientation="vertical" height="80%" bg="gray.300" />

      <Box minW="60px">
        <VStack>
          <HStack>
            <Box flex="none" rounded="lg" px={1} bg={isOffline ? 'error.50' : 'success.50'}>
              <Text size="xs" fontWeight={500} color={isOffline ? 'error.600' : 'success.600'}>
                {t('store_settings:sync_api_service')}: {importerStatus}
              </Text>
            </Box>
          </HStack>
          {isImporterEnabled && !!lastImportDateTime && (
            <HStack space={1} alignItems="center">
              <ClockRefreshIcon size={4} color={colors.gray[700]} />
              <Text size="sm" fontWeight={700} color="gray.700">
                {lastImportDateTimeText}
              </Text>
            </HStack>
          )}
        </VStack>
      </Box>

      <Popover
        isKeyboardDismissable
        trigger={popoverProps => (
          <IconButton
            mx={0.5}
            p={0}
            _hover={{ bg: 'transparent' }}
            icon={<InfoCircleIcon size={4} color={colors.gray[700]} />}
            {...popoverProps}
          />
        )}
      >
        <Popover.Content accessibilityLabel={t('store_settings:sync_api_service')} rounded="lg">
          <Popover.Arrow zIndex={1} />
          <Popover.Body p={3} w="280">
            <VStack space={2}>
              <HStack>
                <Box rounded="lg" py={0.5} px={1.5} bg={isImporterEnabled ? (isOffline ? 'error.50' : 'success.50') : 'gray.50'}>
                  <Text size="md" fontWeight={500} color={isImporterEnabled ? (isOffline ? 'error.600' : 'success.600') : 'gray.600'}>
                    {t('store_settings:sync_api_service')}: {importerStatus}
                  </Text>
                </Box>
              </HStack>

              <HStack>
                <Box pt={0.5} pr={2}>
                  <ClockRefreshIcon size="18px" color={colors.gray[700]} />
                </Box>
                <VStack>
                  <Text size="sm" fontWeight={600} color="gray.700">
                    {t('store_settings:sync_last_data_sync')}
                  </Text>
                  <Text size="sm" fontWeight={700} color="black">
                    {isImporterEnabled && !!lastImportDateTime ? lastImportDateTimeText : '—'}
                  </Text>
                </VStack>
              </HStack>
            </VStack>
          </Popover.Body>
        </Popover.Content>
      </Popover>
    </HStack>
  );
};
